import { authService } from "@/app/services/AuthService";
import { baseUrl } from "@/request";

export interface SSEOptions {
  /**
   * SSE endpoint URL
   */
  url: string;
  /**
   * Request body data
   */
  data?: Record<string, unknown>;
  /**
   * Custom headers
   */
  headers?: Record<string, string>;
  /**
   * Enable automatic reconnection
   * @default true
   */
  autoReconnect?: boolean;
  /**
   * Maximum number of reconnection attempts
   * @default 3
   */
  maxReconnectAttempts?: number;
  /**
   * Reconnection delay in milliseconds
   * @default 1000
   */
  reconnectInterval?: number;
  /**
   * Connection timeout in milliseconds
   * @default 30000
   */
  timeout?: number;
  /**
   * HTTP method
   * @default "POST"
   */
  method?: "GET" | "POST";
}

export interface SSECallbacks {
  /**
   * Called when receiving a message
   */
  onMessage?: (data: string) => void;
  /**
   * Called when the connection is opened
   */
  onOpen?: () => void;
  /**
   * Called when an error occurs
   */
  onError?: (error: Error) => void;
  /**
   * Called when the connection is closed
   */
  onClose?: () => void;
}

export class SSEClient {
  private abortController: AbortController | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private isManualClose = false;
  private options: Required<Omit<SSEOptions, "data" | "headers" | "method">> & {
    data?: Record<string, unknown>;
    headers?: Record<string, string>;
    method: "GET" | "POST";
  };
  private callbacks: SSECallbacks;

  constructor(options: SSEOptions, callbacks: SSECallbacks = {}) {
    this.options = {
      url: options.url,
      data: options.data,
      headers: options.headers,
      autoReconnect: options.autoReconnect ?? true,
      maxReconnectAttempts: options.maxReconnectAttempts ?? 3,
      reconnectInterval: options.reconnectInterval ?? 1000,
      timeout: options.timeout ?? 30000,
      method: options.method ?? "POST",
    };
    this.callbacks = callbacks;
  }

  /**
   * Connect to SSE endpoint
   */
  async connect(): Promise<void> {
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();
    this.isManualClose = false;

    const finalUrl = this.options.url.startsWith("http")
      ? this.options.url
      : `${baseUrl}${this.options.url}`;

    const headers = new Headers({
      Accept: "text/event-stream",
      "Cache-Control": "no-cache",
      Connection: "keep-alive",
      ...this.options.headers,
    });

    // Add authorization token if available
    const token = authService.getToken();
    if (token) {
      headers.set("Authorization", `${token}`);
    }

    // Add content type for POST requests
    if (this.options.method === "POST") {
      headers.set("Content-Type", "application/json");
    }

    const fetchOptions: RequestInit = {
      method: this.options.method,
      headers,
      signal: this.abortController.signal,
    };

    // Add body for POST requests
    if (this.options.method === "POST" && this.options.data) {
      fetchOptions.body = JSON.stringify(this.options.data);
    }

    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_ROBOT_API_HOST}/gw/robot/sse/connect`,
        fetchOptions,
      );

      if (!response.ok) {
        throw new Error(
          `HTTP Error: ${response.status} ${response.statusText}`,
        );
      }

      if (!response.body) {
        throw new Error("Response body is null");
      }

      // Connection established successfully
      this.reconnectAttempts = 0;
      this.callbacks.onOpen?.();

      // Read the stream
      await this.readStream(response.body);
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === "AbortError") {
          return;
        }

        this.callbacks.onError?.(error);

        // Attempt to reconnect if enabled and not manually closed
        if (
          this.options.autoReconnect &&
          !this.isManualClose &&
          this.reconnectAttempts < this.options.maxReconnectAttempts
        ) {
          this.scheduleReconnect();
        } else {
          this.callbacks.onClose?.();
        }
      }
    }
  }

  /**
   * Read the SSE stream
   */
  private async readStream(body: ReadableStream<Uint8Array>): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");

        // Keep the last incomplete line in the buffer
        buffer = lines.pop() || "";

        for (const line of lines) {
          this.processLine(line);
        }
      }

      // Process any remaining data in buffer
      if (buffer.trim()) {
        this.processLine(buffer);
      }

      // Stream ended normally
      if (!this.isManualClose) {
        this.callbacks.onClose?.();

        // Attempt to reconnect if enabled
        if (
          this.options.autoReconnect &&
          this.reconnectAttempts < this.options.maxReconnectAttempts
        ) {
          this.scheduleReconnect();
        }
      }
    } catch (error) {
      if (
        error instanceof Error &&
        error.name !== "AbortError" &&
        !this.isManualClose
      ) {
        this.callbacks.onError?.(error);

        if (
          this.options.autoReconnect &&
          this.reconnectAttempts < this.options.maxReconnectAttempts
        ) {
          this.scheduleReconnect();
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Process a single line from the SSE stream
   */
  private processLine(line: string): void {
    const trimmedLine = line.trim();

    // Ignore empty lines and comments
    if (!trimmedLine || trimmedLine.startsWith(":")) {
      return;
    }

    // Parse SSE format: field: value
    if (trimmedLine.startsWith("data:")) {
      const data = trimmedLine.slice(5).trim();
      if (data) {
        this.callbacks.onMessage?.(data);
      }
    }
  }

  /**
   * Schedule a reconnection attempt
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectAttempts++;
    const delay = this.options.reconnectInterval * this.reconnectAttempts;

    this.reconnectTimer = setTimeout(() => {
      this.connect().catch(() => {});
    }, delay);
  }

  /**
   * Close the SSE connection
   */
  close(): void {
    this.isManualClose = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    this.callbacks.onClose?.();
  }

  /**
   * Check if the connection is active
   */
  isConnected(): boolean {
    return this.abortController !== null && !this.isManualClose;
  }
}

/**
 * Create and connect to an SSE endpoint
 */
export function createSSEConnection(
  options: SSEOptions,
  callbacks: SSECallbacks = {},
): SSEClient {
  const client = new SSEClient(options, callbacks);
  client.connect();
  return client;
}
